<template>
  <div class="max-w-lg w-full mx-auto px-4 flex flex-col">
    <!-- Progress Bar -->
    <div
      v-if="isMultiStep"
      class="w-full bg-gray-200 rounded-full h-1.5 order-first absolute top-0 left-0"
    >
      <div class="bg-blue-500 h-1.5" :style="{ width: progress + '%' }"></div>
    </div>

    <!-- QUB Logo -->
    <div v-if="organiserSlug === 'qub'" class="justify-center">
      <img
        class="mx-auto h-50"
        src="https://imgs.search.brave.com/cUc6ZH6FjB78kcNkN6l4QEioNddtZ7K55mnHlOk4sHE/rs:fit:860:0:0/g:ce/aHR0cHM6Ly9pLnBp/bmltZy5jb20vb3JpZ2lu/YWxzL2Y1L2ZmL2U4/L2Y1ZmZlODliYjRl/NTFlNDZiZjQ4OTQ2/NWJkNzJkOGVkLmpw/Zw"
        width="25%"
        alt="QUB Logo"
      />
    </div>
    <!-- Single Step Form -->
    <div v-if="!isMultiStep">
      <FormSection
        :form="form"
        ref="formSection"
        :events="events"
        :additional-fields="applyConditions()"
        :hideEvent="hideEvent"
        :survey-title="surveyTitle"
        :survey-description="surveyDescription"
        :submit-disabled="submitting"
        @submit="submit"
        @graduation-date-change="onGraduationDateChange"
      />
    </div>

    <!-- Multi Step Form -->
    <div v-else>
      <!-- Multi Step Form -->
      <div v-if="isMultiStep">
        <!-- Step 1: Your Details -->
        <div v-if="currentStep === 1">
          <FormSection
            :form="form"
            ref="formSection"
            :events="events"
            :additional-fields="[]"
            :hideEvent="hideEvent"
            :survey-title="surveyTitle"
            :survey-description="surveyDescription"
            @graduation-date-change="onGraduationDateChange"
            @submit="nextStep"
            submit-button-text="Continue"
          />
        </div>

        <!-- Step 2: Graduation Details -->
        <div v-if="currentStep === 2">
          <button
            type="button"
            v-if="forceStep < 2"
            @click="previousStep"
            class="mt-4 mb-4 text-blue-600 hover:text-blue-700 flex items-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 mr-1"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                clip-rule="evenodd"
              />
            </svg>
            Back
          </button>
          <FormSection
            :form="form"
            :events="events"
            :additional-fields="applyConditions()"
            :hideEvent="hideEvent"
            :survey-title="surveyTitle"
            :survey-description="surveyDescription"
            :submit-disabled="submitting"
            @graduation-date-change="onGraduationDateChange"
            @submit="submit"
            submit-button-text="Continue"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { extend } from "vee-validate";
import {
  required,
  min,
  max,
  email,
  regex,
  confirmed,
} from "vee-validate/dist/rules";
import FormSection from "./FormSection.vue";
import { applySurveyConditions } from "../../surveys/conditions.js";

// Validation rules
extend("personal_email", {
  message: (field) =>
    "The " +
    field +
    " value is not a personal email, please enter an email not ending in ac.uk.",
  validate: (value) => !/^.*((\.ac\.uk)|(bpp\.com))$/.test(value),
});

extend("required", {
  ...required,
  message: "This field is required",
});
extend("min", min);
extend("confirmed", {
  ...confirmed,
  message: "Your email addresses do not match",
});
extend("regex", regex);
extend("max", max);
extend("email", email);

export default {
  name: "RegistrationForm",
  components: {
    FormSection,
  },
  props: {
    events: {
      type: Array,
      required: true,
    },
    additionalFieldsInput: {
      type: Array,
      required: true,
    },
    name: {
      type: String,
      default: "",
    },
    event: {
      type: String,
      default: "",
    },
    email: {
      type: String,
      default: "",
    },
    organiserSlug: {
      type: String,
      required: true,
    },
    forceStep: {
      type: Number,
      default: 1,
    },
    hideEvent: {
      type: Boolean,
      default: false,
    },
    submitting: {
      type: Boolean,
      default: false,
    },
    surveyTitle: {
      type: String,
      default: "",
    },
    surveyDescription: {
      type: String,
      default: "",
    },
  },
  watch: {
    forceStep(newVal) {
      this.currentStep = newVal;
      this.progress = newVal === 2 ? 66 : 33;
    },
    additionalFieldsInput: {
      deep: true,
      handler(fields) {
        this.initializeAdditionalFields(fields);
      },
    },
  },
  data() {
    return {
      currentStep: 1,
      progress: 33,
      form: {
        name: this.name || "",
        event: this.event || null,
        email: this.email || "",
        confirmEmail: this.email || "",
        additionalFields: [],
      },
    };
  },
  computed: {
    isMultiStep() {
      return process.env.VUE_APP_FORM_MODE === "multi";
    },
  },
  methods: {
    nextStep() {
      if (this.currentStep < 2) {
        this.currentStep++;
        this.progress = Math.min(this.progress + 33, 100); // Update progress
      }
    },
    previousStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
        this.progress = Math.max(this.progress - 33, 33); // Update progress
      }
    },
    onGraduationDateChange(eventId) {
      this.$emit("graduation-date-change", eventId);
    },
    resetForm() {
      this.currentStep = 1;
      this.progress = 33;
      this.form = {
        name: "",
        email: "",
        confirmEmail: "",
        event: null,
        additionalFields: [],
      };
    },
    initializeAdditionalFields(fields) {
      const currentValues = new Map(
        this.form.additionalFields.map((field) => [field.name, field.value])
      );
      this.form.additionalFields = (fields || []).filter(field => !["markdown", "presentationOnly"].includes(field.type)).map((field) => {
        const copy = JSON.parse(JSON.stringify(field));
        if (currentValues.has(field.name)) copy.value = currentValues.get(field.name);
        else if (!("value" in copy)) copy.value = field.type === "multi-select" ? [] : null;
        if (copy.regex != null) copy.regex = new RegExp(copy.regex);
        return copy;
      });
    },

    submit() {
      if (this.submitting) {
        return;
      }
      this.$emit("submit", this.form);
    },
    getField(field) {
      return this.form.additionalFields.find((f) => f.name === field.name);
    },
    applyConditions() {
      return applySurveyConditions(
        this.additionalFieldsInput,
        this.form.additionalFields,
        this.form,
      );
    },
  },
  created() {
    this.currentStep = this.forceStep;
    // Initialize form
    this.initializeAdditionalFields(this.additionalFieldsInput);
  },
};
</script>

<style scoped>
.markdownHtmlCustom a {
  text-decoration: underline !important;
}
</style>
